import * as React from 'react';

import cx from 'classnames';

import Layer from 'components/Layer';
import withLayerAutoFocus from 'components/lib/withLayerAutoFocus';
import createChainedFunction from 'utils/createChainedFunction';

import './styles/Menu.scss';

/**
 * ```example
 * <Menu>
 *   <MenuItem href="#">
 *     <Glyph name="remind-icon" size="large" />
 *     <span>MenuItem</span>
 *   </MenuItem>
 *   <MenuItem href="#">
 *     <Glyph name="remind-icon" size="large" />
 *     <span>MenuItem</span>
 *   </MenuItem>
 *   <MenuItem href="#">
 *     <Glyph name="remind-icon" size="large" />
 *     <span>MenuItem</span>
 *   </MenuItem>
 * </Menu>
 * ```
 */

type MenuWidth = 'medium';

export type Props = {
  width?: MenuWidth;
  hideOnSelect?: boolean;
  rowHover?: boolean;
  horizontal?: boolean;
  selectedKey?: string;
  title?: string;
  onSelect?: (key: string) => void;
  children?: any;
  className?: string;
  onRequestHide?: () => void;
};

type DefaultProps = {
  hideOnSelect: boolean;
  selectedKey: string;
};

class Menu extends React.Component<Props & DefaultProps> {
  static defaultProps: DefaultProps = {
    hideOnSelect: true,
    selectedKey: '',
  };

  handleMenuItemSelect = (key: string) => {
    if (this.props.onSelect) {
      this.props.onSelect(key);
    }
  };

  renderMenuItem = (menuItem: React.ReactElement<any>) => {
    if (menuItem === null) {
      return null;
    }

    const key = menuItem.props.menuKey || menuItem.key;
    const onClick = this.props.hideOnSelect
      ? createChainedFunction(menuItem.props.onClick, this.props.onRequestHide)
      : menuItem.props.onClick;
    return React.cloneElement(menuItem, {
      menuKey: key,
      ref: menuItem.props.ref,
      selected: this.props.selectedKey ? this.props.selectedKey === key : null,
      onClick,
      onSelect: createChainedFunction(menuItem.props.onSelect, this.handleMenuItemSelect),
    });
  };

  renderTitle = () => {
    const { title } = this.props;
    if (!title) {
      return null;
    }
    return <h2 className="Menu-title">{title}</h2>;
  };

  render() {
    const { children, className, horizontal, width, rowHover, ...props } = this.props;

    return (
      <Layer
        {...props}
        propagateOutsideClicks={false}
        className={cx(
          'Menu',
          className,
          horizontal && 'Menu--horizontal',
          width && `Menu--${width}`,
          rowHover && 'Menu--rowHover'
        )}
      >
        <div className="Menu-dropdown">
          {this.renderTitle()}
          <ul className="Menu-list">{React.Children.map(children, this.renderMenuItem)}</ul>
        </div>
      </Layer>
    );
  }
}

export { Menu };
export default withLayerAutoFocus<Props>(Menu);
